import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, Loader2, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Camera capture + file upload for identity and document evidence.
 */
export default function DocumentCaptureField({
  label = 'Add document',
  accept = '.pdf,.jpg,.jpeg,.png,.webp',
  multiple = false,
  uploading = false,
  onFileSelected,
  className = '',
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mode, setMode] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = async () => {
    setCameraError(null);
    setMode('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      setCameraError('Camera unavailable — use upload instead.');
      setMode(null);
      stopCamera();
    }
  };

  const closeCamera = () => {
    stopCamera();
    setMode(null);
    setPreviewUrl(null);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setPreviewUrl(URL.createObjectURL(blob));
        onFileSelected?.(multiple ? [file] : file);
        closeCamera();
      },
      'image/jpeg',
      0.92,
    );
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (multiple) {
      onFileSelected?.(files);
    } else {
      onFileSelected?.(files[0]);
    }
    event.target.value = '';
  };

  return (
    <div className={className} data-testid="document-capture-field">
      {mode === 'camera' ? (
        <div className="rounded-xl border border-slate-200 bg-slate-900 p-3">
          <div className="relative overflow-hidden rounded-lg bg-black">
            <video
              ref={videoRef}
              playsInline
              muted
              className="aspect-[4/3] w-full object-cover"
              data-testid="document-capture-video"
            />
            {previewUrl ? (
              <img src={previewUrl} alt="Captured preview" className="absolute inset-0 h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              className="gap-1.5 rounded-xl bg-white text-slate-900 hover:bg-slate-100"
              onClick={capturePhoto}
              disabled={uploading}
              data-testid="document-capture-shutter"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              Take photo
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5 rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10"
              onClick={closeCamera}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5 rounded-xl"
            onClick={startCamera}
            disabled={uploading}
            data-testid="document-capture-camera-btn"
          >
            <Camera className="h-4 w-4" />
            {label ? `Photo — ${label}` : 'Take photo'}
          </Button>
          <Button
            type="button"
            size="sm"
            className="gap-1.5 rounded-xl"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="document-capture-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            className="hidden"
            onChange={handleFileChange}
            data-testid="document-capture-file-input"
          />
        </div>
      )}
      {cameraError ? (
        <p className="mt-2 flex items-center gap-1 text-xs text-amber-700">
          <ImagePlus className="h-3.5 w-3.5" />
          {cameraError}
        </p>
      ) : null}
    </div>
  );
}